import cx from 'classnames'
import { AriaAttributes } from 'react'
import * as React from 'react'
import styles from './BillingItemList.sass'

interface BillingItemListProps {
  children?: React.ReactNode
}

/**
 " Billing Item List
 */
export function BillingItemList({ children }: BillingItemListProps) {
  return <div className={styles.root}>{children}</div>
}

interface BillingItemProps {
  children?: React.ReactNode
  onClick?: () => unknown
  disabled?: boolean
  ['data-test']?: string
  ['aria-selected']?: AriaAttributes['aria-selected']
}

/**
 " Billing Item
 */
export function BillingItem({
  children,
  disabled,
  onClick,
  'data-test': dataTest,
  'aria-selected': ariaSelected,
}: BillingItemProps) {
  const click = disabled ? undefined : onClick
  return (
    <div
      data-test={dataTest}
      aria-selected={ariaSelected}
      className={cx(
        styles.item,
        click && styles.clickable,
        disabled && styles.disabled,
      )}
      onClick={click}
    >
      {children}
      <div className={styles.shadow} />
    </div>
  )
}

interface BillingItemVisualProps {
  children?: React.ReactNode
}

/**
 " Billing Item Visual
 */
export function BillingItemVisual({ children }: BillingItemVisualProps) {
  return <div className={styles.visual}>{children}</div>
}

interface BillingItemContentProps {
  children?: React.ReactNode
}

/**
 " Billing Item Content
 */
export function BillingItemContent({ children }: BillingItemContentProps) {
  return <div className={styles.content}>{children}</div>
}

interface BillingItemTitleProps {
  children?: React.ReactNode
  ['data-test']?: string
}

/**
 " Billing Item Title
 */
export function BillingItemTitle({
  children,
  'data-test': dataTest,
}: BillingItemTitleProps) {
  return (
    <div data-test={dataTest} className={styles.title}>
      {children}
    </div>
  )
}

interface BillingItemSubtitleProps {
  children?: React.ReactNode
  ['data-test']?: string
}

/**
 " Billing Item Subtitle
 */
export function BillingItemSubtitle({
  children,
  'data-test': dataTest,
}: BillingItemSubtitleProps) {
  return (
    <div data-test={dataTest} className={styles.subtitle}>
      {children}
    </div>
  )
}

interface BillingItemExtrasProps {
  children?: React.ReactNode
}

/**
 " Billing Item Extras
 */
export function BillingItemExtras({ children }: BillingItemExtrasProps) {
  return <div className={styles.extras}>{children}</div>
}

interface BillingItemBadgeProps {
  children?: React.ReactNode
}

/**
 " Billing Item Badge
 */
export function BillingItemBadge({ children }: BillingItemBadgeProps) {
  return <div className={styles.badge}>{children}</div>
}

interface BillingItemActionsProps {
  children?: React.ReactNode
}

/**
 " Billing Item Actions
 */
export function BillingItemActions({ children }: BillingItemActionsProps) {
  return <div className={styles.actions}>{children}</div>
}

interface BillingItemActionProps {
  children?: React.ReactNode
  hoverOnly?: boolean
}

/**
 " Billing Item Action
 */
export function BillingItemAction({
  children,
  hoverOnly,
}: BillingItemActionProps) {
  const handleClick = React.useCallback((e: React.MouseEvent<unknown>) => {
    e.stopPropagation()
  }, [])
  return (
    <div
      className={cx(styles.action, hoverOnly && styles.hoverOnly)}
      onClick={handleClick}
    >
      {children}
    </div>
  )
}
